// admin/src/pages/Sessions.jsx - WITH ADVANCED LEAD FILTERING
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import TopNav from '../components/TopNav';
import SessionDetailModal from '../components/SessionDetailModal';
import { useAdminAuth } from '../auth/AdminAuthContext';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Sessions() {
  const { secret } = useAdminAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  
  // Advanced filter states
  const [filterDateRange, setFilterDateRange] = useState('all'); // all, today, 7days, 30days, 90days, custom
  const [filterCustomStartDate, setFilterCustomStartDate] = useState('');
  const [filterCustomEndDate, setFilterCustomEndDate] = useState('');
  const [filterMinResponses, setFilterMinResponses] = useState('');
  const [filterHasEmail, setFilterHasEmail] = useState(''); // '', 'true', 'false'
  const [filterEmailOptIn, setFilterEmailOptIn] = useState(''); // '', 'true', 'false'
  const [filterHasName, setFilterHasName] = useState(''); // '', 'true', 'false'
  const [filterDegreeLevel, setFilterDegreeLevel] = useState('');
  const [filterAgeRange, setFilterAgeRange] = useState('');
  
  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Modal states
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const didFetchRef = useRef(false);

  // Fetch sessions
  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_ROOT}/api/sessions?tenantId=demo-university&limit=500`,
        {
          headers: { 'x-admin-secret': secret || '' }
        }
      );
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      } else {
        setError('Failed to load sessions');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [secret]);

  useEffect(() => {
    if (didFetchRef.current) return;
    if (!secret) return;
    didFetchRef.current = true;
    loadSessions();
  }, [loadSessions, secret]);

  // Export to CSV with active filters
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        tenantId: 'demo-university',
        format: 'csv'
      });

      // Apply date range filter
      if (filterDateRange !== 'all') {
        if (filterDateRange === 'custom') {
          if (filterCustomStartDate) params.append('startDate', filterCustomStartDate);
          if (filterCustomEndDate) params.append('endDate', filterCustomEndDate);
        } else {
          const now = new Date();
          const days = {
            'today': 0,
            '7days': 7,
            '30days': 30,
            '90days': 90
          }[filterDateRange];
          
          if (days === 0) {
            // Today only
            const startOfDay = new Date(now.setHours(0, 0, 0, 0));
            params.append('startDate', startOfDay.toISOString());
          } else {
            const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
            params.append('startDate', startDate.toISOString());
          }
        }
      }

      // Apply other filters
      if (filterMinResponses) params.append('minResponses', filterMinResponses);
      if (filterHasEmail) params.append('hasEmail', filterHasEmail);
      if (filterEmailOptIn) params.append('emailOptIn', filterEmailOptIn);
      if (filterHasName) params.append('hasName', filterHasName);
      if (filterDegreeLevel) params.append('degreeLevel', filterDegreeLevel);
      if (filterAgeRange) params.append('ageRange', filterAgeRange);

      const url = `${API_ROOT}/api/sessions/export?${params.toString()}`;
      
      const res = await fetch(url, {
        headers: { 'x-admin-secret': secret }
      });

      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `sessions-export-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const text = await res.text();
        alert('Export failed: ' + text);
      }
    } catch (err) {
      alert('Export error: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Copy API feed URL
  const handleCopyFeedURL = () => {
    const feedUrl = `${API_ROOT}/api/sessions/feed?tenantId=demo-university`;
    navigator.clipboard.writeText(feedUrl);
    alert('Feed URL copied to clipboard!\n\nUse this URL in Slate, HubSpot, or other platforms.\nAdd &since=TIMESTAMP for incremental updates.');
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchText('');
    setFilterDateRange('all');
    setFilterCustomStartDate('');
    setFilterCustomEndDate('');
    setFilterMinResponses('');
    setFilterHasEmail('');
    setFilterEmailOptIn('');
    setFilterHasName('');
    setFilterDegreeLevel('');
    setFilterAgeRange('');
  };

  // Filter and sort sessions
  const filteredSessions = useMemo(() => {
    let result = sessions;
    
    // Search filter
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(s =>
        (s.sessionId || '').toLowerCase().includes(q) ||
        (s.firstName || '').toLowerCase().includes(q) ||
        (s.lastName || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.degreeLevel || '').toLowerCase().includes(q) ||
        (s.personalizedTitle || '').toLowerCase().includes(q)
      );
    }

    // Date range filter
    if (filterDateRange !== 'all') {
      const now = new Date();
      let startDate;

      if (filterDateRange === 'custom') {
        if (filterCustomStartDate) {
          startDate = new Date(filterCustomStartDate);
          result = result.filter(s => {
            const sessionDate = new Date(s.computedAt || s.createdAt);
            return sessionDate >= startDate;
          });
        }
        if (filterCustomEndDate) {
          const endDate = new Date(filterCustomEndDate);
          result = result.filter(s => {
            const sessionDate = new Date(s.computedAt || s.createdAt);
            return sessionDate <= endDate;
          });
        }
      } else {
        const days = {
          'today': 0,
          '7days': 7,
          '30days': 30,
          '90days': 90
        }[filterDateRange];

        if (days === 0) {
          startDate = new Date(now.setHours(0, 0, 0, 0));
        } else {
          startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        }

        result = result.filter(s => {
          const sessionDate = new Date(s.computedAt || s.createdAt);
          return sessionDate >= startDate;
        });
      }
    }

    // Min responses filter
    if (filterMinResponses) {
      result = result.filter(s => (s.responsesCount || 0) >= Number(filterMinResponses));
    }

    // Has email filter
    if (filterHasEmail === 'true') {
      result = result.filter(s => s.email);
    } else if (filterHasEmail === 'false') {
      result = result.filter(s => !s.email);
    }

    // Email opt-in filter
    if (filterEmailOptIn === 'true') {
      result = result.filter(s => s.emailOptIn);
    } else if (filterEmailOptIn === 'false') {
      result = result.filter(s => !s.emailOptIn);
    }

    // Has name filter
    if (filterHasName === 'true') {
      result = result.filter(s => s.firstName);
    } else if (filterHasName === 'false') {
      result = result.filter(s => !s.firstName);
    }

    // Degree level filter
    if (filterDegreeLevel) {
      result = result.filter(s => s.degreeLevel === filterDegreeLevel);
    }

    // Age range filter
    if (filterAgeRange) {
      result = result.filter(s => s.ageRange === filterAgeRange);
    }
    
    // Sort
    switch (sortBy) {
      case 'recent':
        result.sort((a, b) => new Date(b.computedAt || b.createdAt) - new Date(a.computedAt || a.createdAt));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.computedAt || a.createdAt) - new Date(b.computedAt || b.createdAt));
        break;
      case 'responses':
        result.sort((a, b) => (b.responsesCount || 0) - (a.responsesCount || 0));
        break;
      case 'quality':
        result.sort((a, b) => {
          // Anonymous (no email AND no name) always last
          const isAnonA = !a.email && !a.firstName;
          const isAnonB = !b.email && !b.firstName;
          if (isAnonA && !isAnonB) return 1;
          if (!isAnonA && isAnonB) return -1;
          if (isAnonA && isAnonB) return 0;

          // Priority 1: Has email
          const emailA = a.email ? 1 : 0;
          const emailB = b.email ? 1 : 0;
          if (emailB !== emailA) return emailB - emailA;

          // Priority 2: Opted in
          const optInA = a.emailOptIn ? 1 : 0;
          const optInB = b.emailOptIn ? 1 : 0;
          if (optInB !== optInA) return optInB - optInA;

          // Priority 3: Has name
          const nameA = a.firstName ? 1 : 0;
          const nameB = b.firstName ? 1 : 0;
          if (nameB !== nameA) return nameB - nameA;

          // Priority 4: Most responses
          return (b.responsesCount || 0) - (a.responsesCount || 0);
        });
        break;
      default:
        break;
    }
    
    return result;
  }, [sessions, searchText, sortBy, filterDateRange, filterCustomStartDate, filterCustomEndDate, 
      filterMinResponses, filterHasEmail, filterEmailOptIn, filterHasName, filterDegreeLevel, filterAgeRange]);

  // Get unique values for dropdowns
  const uniqueDegreeLevels = useMemo(() => {
    const levels = new Set();
    sessions.forEach(s => s.degreeLevel && levels.add(s.degreeLevel));
    return Array.from(levels).sort();
  }, [sessions]);

  const uniqueAgeRanges = useMemo(() => {
    const ranges = new Set();
    sessions.forEach(s => s.ageRange && ranges.add(s.ageRange));
    return Array.from(ranges).sort();
  }, [sessions]);

  // Calculate filter status
  const hasActiveFilters = searchText || filterDateRange !== 'all' || filterMinResponses || 
    filterHasEmail || filterEmailOptIn || filterHasName || filterDegreeLevel || filterAgeRange;

  const handleViewDetails = (session) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedSession(null);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="adm-page">
      <TopNav />
      
      <div className="adm-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px' }}>
        {/* Header with Export Actions */}
        <header style={{ marginBottom: 24 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: 12,
            gap: 16,
            flexWrap: 'wrap'
          }}>
            <div>
              <h1 style={{ margin: 0, marginBottom: 8 }}>Sessions</h1>
              <p style={{ margin: 0, color: '#6b7280' }}>
                Filter and export qualified leads
              </p>
            </div>
            
            {/* Export Actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleCopyFeedURL}
                style={{
                  padding: '10px 16px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                🔗 Copy Feed URL
              </button>
              
              <button
                onClick={handleExportCSV}
                disabled={exporting || filteredSessions.length === 0}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: (exporting || filteredSessions.length === 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  opacity: (exporting || filteredSessions.length === 0) ? 0.5 : 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {exporting ? '⏳ Exporting...' : `📥 Export ${filteredSessions.length} Sessions`}
              </button>
            </div>
          </div>

          {/* Export Info Banner */}
          {hasActiveFilters && filteredSessions.length > 0 && (
            <div style={{
              padding: 12,
              background: '#eff6ff',
              border: '1px solid #dbeafe',
              borderRadius: 8,
              fontSize: '0.85rem',
              color: '#1e40af',
              marginBottom: 16
            }}>
              💡 <strong>Active filters will be applied to export.</strong> You're exporting {filteredSessions.length} of {sessions.length} total sessions.
            </div>
          )}
        </header>

        {/* Advanced Filters Panel */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 16
          }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              🔍 Filter Leads
            </h3>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                ✖ Clear All
              </button>
            )}
          </div>

          {/* Filter Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginBottom: 12
          }}>
            {/* Search */}
            <input
              type="text"
              placeholder="🔍 Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            />

            {/* Date Range */}
            <select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="all">📅 All Time</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="recent">🕐 Most Recent</option>
              <option value="oldest">🕐 Oldest First</option>
              <option value="responses">📊 Most Responses</option>
              <option value="quality">⭐ Best Leads First</option>
            </select>

            {/* Min Responses */}
            <input
              type="number"
              placeholder="Min Responses"
              value={filterMinResponses}
              onChange={(e) => setFilterMinResponses(e.target.value)}
              min="0"
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            />

            {/* Has Email */}
            <select
              value={filterHasEmail}
              onChange={(e) => setFilterHasEmail(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="">📧 Email Status</option>
              <option value="true">✅ Has Email</option>
              <option value="false">❌ No Email</option>
            </select>

            {/* Email Opt-in */}
            <select
              value={filterEmailOptIn}
              onChange={(e) => setFilterEmailOptIn(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="">✉️ Opt-in Status</option>
              <option value="true">✅ Opted In</option>
              <option value="false">❌ Not Opted In</option>
            </select>

            {/* Has Name */}
            <select
              value={filterHasName}
              onChange={(e) => setFilterHasName(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="">👤 Name Status</option>
              <option value="true">✅ Has Name</option>
              <option value="false">❌ Anonymous</option>
            </select>

            {/* Degree Level */}
            <select
              value={filterDegreeLevel}
              onChange={(e) => setFilterDegreeLevel(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="">🎓 Degree Level</option>
              {uniqueDegreeLevels.map(level => (
                <option key={level} value={level}>
                  {level.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>

            {/* Age Range */}
            <select
              value={filterAgeRange}
              onChange={(e) => setFilterAgeRange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.9rem'
              }}
            >
              <option value="">👥 Age Range</option>
              {uniqueAgeRanges.map(range => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Range */}
          {filterDateRange === 'custom' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
              marginTop: 12,
              padding: 12,
              background: '#f9fafb',
              borderRadius: 8
            }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 4, display: 'block' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={filterCustomStartDate}
                  onChange={(e) => setFilterCustomStartDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 4, display: 'block' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={filterCustomEndDate}
                  onChange={(e) => setFilterCustomEndDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: '0.9rem',
                    width: '100%'
                  }}
                />
              </div>
            </div>
          )}

          {/* Filter Summary */}
          <div style={{
            marginTop: 12,
            padding: 12,
            background: '#f9fafb',
            borderRadius: 8,
            fontSize: '0.875rem',
            color: '#374151'
          }}>
            Showing <strong style={{ color: '#0ea5e9' }}>{filteredSessions.length}</strong> of <strong>{sessions.length}</strong> total sessions
            {hasActiveFilters && <span style={{ color: '#10b981', marginLeft: 8 }}>• Filters active</span>}
          </div>
        </div>

        {/* Stats */}
        <div style={{ 
          display: 'flex', 
          gap: 16, 
          marginBottom: 20,
          flexWrap: 'wrap'
        }}>
          <div style={{ 
            padding: '12px 16px',
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            flex: '1 1 150px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>
              Total Sessions
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
              {filteredSessions.length}
            </div>
          </div>
          
          <div style={{ 
            padding: '12px 16px',
            background: '#f0fdf4',
            borderRadius: 8,
            border: '1px solid #bbf7d0',
            flex: '1 1 150px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#166534', marginBottom: 4 }}>
              With Email
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#166534' }}>
              {filteredSessions.filter(s => s.email).length}
            </div>
          </div>

          <div style={{ 
            padding: '12px 16px',
            background: '#eff6ff',
            borderRadius: 8,
            border: '1px solid #bfdbfe',
            flex: '1 1 150px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#1e40af', marginBottom: 4 }}>
              Opted In
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e40af' }}>
              {filteredSessions.filter(s => s.emailOptIn).length}
            </div>
          </div>

          <div style={{ 
            padding: '12px 16px',
            background: '#fef3c7',
            borderRadius: 8,
            border: '1px solid #fde68a',
            flex: '1 1 150px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: 4 }}>
              High Quality
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#92400e' }}>
              {filteredSessions.filter(s => 
                (s.firstName || s.email) && // Must have name OR email (not anonymous)
                s.email && 
                s.emailOptIn && 
                (s.responsesCount || 0) >= 10
              ).length}
            </div>
          </div>

          <button
            onClick={loadSessions}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              flex: '0 0 auto'
            }}
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ 
            padding: 16, 
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
            marginBottom: 20
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            Loading sessions...
          </div>
        )}

        {/* Sessions Grid */}
        {!loading && filteredSessions.length > 0 && (
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16
          }}>
            {filteredSessions.map(session => {
              // Lead quality — mirrors calculateLeadScore() in sessionsExportController.js
              const hasEmail = !!session.email;
              const hasOptIn = !!session.emailOptIn;
              const hasName  = !!session.firstName;
              const isAnonymous = !hasEmail && !hasName;
              // Tier 1 = email + opted in + name (best). Must have email to be high quality.
              const isHighQuality = !isAnonymous && hasEmail && hasOptIn;

              return (
                <article 
                  key={session._id}
                  className="adm-card"
                  style={{ 
                    padding: 16,
                    transition: 'all 0.2s',
                    border: isHighQuality ? '2px solid #10b981' : '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: 'white',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = isHighQuality ? '#10b981' : '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = isHighQuality ? '#10b981' : '#e5e7eb';
                  }}
                >
                  {/* Quality Badge */}
                  {isHighQuality && (
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: '#10b981',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 12,
                      fontSize: '0.7rem',
                      fontWeight: 700
                    }}>
                      ⭐ HIGH QUALITY
                    </div>
                  )}

                  {/* User Info */}
                  <div style={{ marginBottom: 12 }}>
                    {session.firstName ? (
                      <div style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700,
                        color: '#111827',
                        marginBottom: 4
                      }}>
                        {session.firstName} {session.lastName || ''}
                      </div>
                    ) : (
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: 600,
                        color: '#6b7280',
                        marginBottom: 4
                      }}>
                        Anonymous User
                      </div>
                    )}
                    
                    {session.email && (
                      <div style={{ 
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        fontFamily: 'monospace',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}>
                        {session.email}
                        {session.emailOptIn && (
                          <span style={{ 
                            background: '#10b981',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: 10,
                            fontSize: '0.65rem',
                            fontWeight: 700
                          }}>
                            ✓
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quick Info */}
                  <div style={{ 
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginBottom: 12
                  }}>
                    {session.degreeLevel && (
                      <span style={{ 
                        fontSize: '0.75rem',
                        background: '#f3f4f6',
                        color: '#374151',
                        padding: '4px 8px',
                        borderRadius: 4,
                        textTransform: 'capitalize'
                      }}>
                        {session.degreeLevel.replace(/-/g, ' ')}
                      </span>
                    )}
                    
                    {session.ageRange && (
                      <span style={{ 
                        fontSize: '0.75rem',
                        background: '#f3f4f6',
                        color: '#374151',
                        padding: '4px 8px',
                        borderRadius: 4
                      }}>
                        {session.ageRange}
                      </span>
                    )}
                    
                    <span style={{ 
                      fontSize: '0.75rem',
                      background: '#eff6ff',
                      color: '#1e40af',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontWeight: 600
                    }}>
                      {session.responsesCount || 0} responses
                    </span>
                  </div>

                  {/* Title & Date */}
                  <div style={{ marginBottom: 12 }}>
                    {session.personalizedTitle && (
                      <div style={{ 
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#6366f1',
                        marginBottom: 4
                      }}>
                        {session.personalizedTitle}
                      </div>
                    )}
                    
                    <div style={{ 
                      fontSize: '0.75rem',
                      color: '#9ca3af'
                    }}>
                      {formatDate(session.computedAt || session.createdAt)}
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button
                    onClick={() => handleViewDetails(session)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#3b82f6';
                    }}
                  >
                    View Details →
                  </button>
                </article>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredSessions.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: 60,
            background: '#f9fafb',
            borderRadius: 12,
            border: '1px dashed #d1d5db'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>📊</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No sessions found</h3>
            <p style={{ margin: 0, color: '#6b7280' }}>
              {hasActiveFilters ? 'Try adjusting your filters' : 'Sessions will appear here as users complete the quiz'}
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
      />
    </div>
  );
}